import {
  DeepAAgentApiError,
  useDeepAAgentApi,
} from '@/pocs/chat/composables/agent-api'
import type {
  DeepAAgentRunState,
  DeepAAgentStartRunBody,
  DeepAChatMessage,
  DeepAChatServerMessage,
} from '@/pocs/chat/types'

function applyRunState(
  message: DeepAChatServerMessage,
  state: Partial<DeepAAgentRunState>,
) {
  if (state.reply) {
    message.content = state.reply
  }
  const statusUpdate = state.statusUpdates?.at(-1)
  if (statusUpdate) {
    message.statusUpdate = statusUpdate
  }
  if (state.status === 'COMPLETED') {
    message.status = 'finished'
  } else if (state.status === 'ABORTED') {
    message.status = 'aborted'
  } else if (state.status === 'ERROR') {
    message.status = 'error'
    message.error = state.error ?? undefined
  }
}

export function useDeepAChatSession(
  organizationId: MaybeRefOrGetter<string | undefined>,
) {
  const { t } = useI18n()
  const agentApi = useDeepAAgentApi(organizationId)

  const messages = ref<DeepAChatMessage[]>([])
  const loading = ref(false)
  const conversationId = ref('')
  const jobId = ref('')
  let abortController: AbortController | undefined
  // a run that outlives its session must not write into the next one
  let sessionGeneration = 0

  function createCurrentSessionGuard() {
    const generation = sessionGeneration
    return () => generation === sessionGeneration
  }

  function pushExchange(question: string) {
    messages.value.push({ role: 'user', content: question })
    messages.value.push({ role: 'assistant', content: '', status: 'pending' })
    return messages.value.at(-1) as DeepAChatServerMessage
  }

  function beginRun() {
    const controller = new AbortController()
    abortController = controller
    loading.value = true
    return controller
  }

  function endRun(controller: AbortController) {
    if (abortController === controller) {
      abortController = undefined
      loading.value = false
      jobId.value = ''
    }
  }

  function getErrorMessage(error: unknown) {
    return error instanceof DeepAAgentApiError && error.detail
      ? error.detail
      : t('error.error_occurred')
  }

  async function followRunInto(
    reply: DeepAChatServerMessage,
    runJobId: string,
    controller: AbortController,
  ) {
    jobId.value = runJobId
    try {
      const { isDone } = await agentApi.followRun(runJobId, {
        signal: controller.signal,
        onStatus: (state) => applyRunState(reply, state),
        onReply: (text) => {
          reply.content = text
        },
        onDone: (state) => applyRunState(reply, state),
      })
      if (!isDone && reply.status === 'pending') {
        reply.status = 'error'
        reply.error = t('error.error_occurred')
      }
    } catch (error) {
      if (controller.signal.aborted) {
        reply.status = 'aborted'
      } else {
        reply.status = 'error'
        reply.error = getErrorMessage(error)
        agentApi.abortRun(runJobId)
      }
    } finally {
      endRun(controller)
    }
  }

  // leaving a session only stops listening; its run keeps going on the server
  function detach() {
    abortController?.abort()
  }

  function stop() {
    if (jobId.value) {
      agentApi.abortRun(jobId.value)
    }
    abortController?.abort()
  }

  function reset() {
    detach()
    sessionGeneration++
    abortController = undefined
    loading.value = false
    jobId.value = ''
    messages.value = []
    conversationId.value = ''
  }

  async function send(
    message: string,
    scope: Pick<DeepAAgentStartRunBody, 'deepBoxNodeIds' | 'includeHidden'>,
  ) {
    if (loading.value) return

    const reply = pushExchange(message)
    const isCurrentSession = createCurrentSessionGuard()
    const controller = beginRun()

    let run
    try {
      run = await agentApi.startRun({
        message,
        conversationId: conversationId.value || undefined,
        ...scope,
      })
    } catch (error) {
      reply.status = 'error'
      reply.error = getErrorMessage(error)
      endRun(controller)
      return
    }

    if (!isCurrentSession()) return

    conversationId.value = run.conversationId
    if (controller.signal.aborted) {
      agentApi.abortRun(run.jobId)
      reply.status = 'aborted'
      endRun(controller)
      return
    }
    await followRunInto(reply, run.jobId, controller)
  }

  return {
    messages,
    loading,
    send,
    stop,
    detach,
    reset,
  }
}
